import { createFileRoute, Link } from "@tanstack/react-router";
import { ShieldCheck, FileText, MapPin, AlertTriangle, Phone, CheckCircle2 } from "lucide-react";
import Navbar from "@/components/Navbar";
import SeoLongform from "@/components/SeoLongform";
import GeoAnswerBlock from "@/components/GeoAnswerBlock";

const TITLE = "Taking a Sprinter Van Rental into Canada — Insurance Requirements";
const DESC =
  "Drive a rented 12 or 15 passenger Sprinter van from the U.S. into Canada. Renters must provide their own personal auto insurance that covers the rental van in Canada.";

export const Route = createFileRoute("/canada-travel")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:type", content: "article" },
      { property: "og:url", content: "https://sprinter-van-rentals.com/canada-travel" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://sprinter-van-rentals.com/canada-travel" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: [
            {
              "@type": "Question",
              name: "Can I drive a rental Sprinter van into Canada?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Yes. Canada travel is permitted with written authorization on your rental agreement and proof that your own personal auto insurance policy extends to the rented van while in Canada.",
              },
            },
            {
              "@type": "Question",
              name: "What insurance do I need to take a rental van into Canada?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Renters must provide their own personal auto liability insurance that covers the rental van in Canada, along with a Canadian Non-Resident Inter-Province Motor Vehicle Liability Insurance Card (yellow card) from their insurer.",
              },
            },
          ],
        }),
      },
    ],
  }),
  component: CanadaTravelPage,
});

const requirements = [
  {
    icon: ShieldCheck,
    title: "Your own personal auto insurance",
    body: "You must carry a personal auto policy that extends coverage to a rented 12 or 15 passenger van while operating in Canada. Confirm in writing with your agent that both the vehicle class and the country are covered — many policies exclude large passenger vans.",
  },
  {
    icon: FileText,
    title: "Canadian insurance card (yellow card)",
    body: "Ask your insurer for a Canadian Non-Resident Inter-Province Motor Vehicle Liability Insurance Card. Border officers and Canadian police accept it as proof of financial responsibility. Carry the printed card in the van.",
  },
  {
    icon: MapPin,
    title: "Written cross-border authorization",
    body: "Let us know before you book. We add Canada travel permission to your rental agreement so the van is legally authorized to leave the United States.",
  },
  {
    icon: AlertTriangle,
    title: "Passports for every passenger",
    body: "Everyone in the van needs a passport, NEXUS card, or enhanced driver's license. Minors traveling without both parents should carry a notarized consent letter.",
  },
];

function CanadaTravelPage() {
  return (
    <main className="bg-white">
      <Navbar />
      <div className="pt-16">
        <section className="bg-[#0F172A] text-white px-6 py-20 md:py-28">
          <div className="max-w-4xl mx-auto">
            <p className="text-[#F97316] text-xs tracking-[0.3em] uppercase font-body mb-4">
              Cross-Border Rentals
            </p>
            <h1 className="font-display font-black text-4xl md:text-6xl uppercase leading-none tracking-tight">
              Taking a Sprinter into Canada
            </h1>
            <p className="mt-6 text-white/80 text-lg max-w-2xl leading-relaxed">
              Canada trips are welcome in our 12 and 15 passenger vans — with one firm
              condition: you must provide your own personal auto insurance that covers the
              rental van while it's in Canada.
            </p>
            <div className="mt-8 inline-flex flex-col sm:flex-row gap-3">
              <Link
                to="/book"
                className="inline-flex items-center justify-center bg-[#F97316] text-white font-display font-bold text-sm tracking-[0.2em] uppercase px-8 py-4 hover:brightness-110 transition"
              >
                Start a Booking
              </Link>
              <a
                href="tel:+19713282665"
                className="inline-flex items-center justify-center gap-2 border border-white/30 text-white font-display font-bold text-sm tracking-[0.2em] uppercase px-8 py-4 hover:bg-white/10 transition"
              >
                <Phone size={16} /> (971) 328-2665
              </a>
            </div>
          </div>
        </section>

        <section className="px-6 py-16 md:py-20">
          <div className="max-w-4xl mx-auto">
            <div className="border-l-4 border-[#F97316] bg-[#F97316]/5 p-6 md:p-8">
              <h2 className="font-display font-black text-2xl md:text-3xl text-[#0F172A] tracking-tight">
                You must bring your own insurance for Canada
              </h2>
              <p className="mt-4 text-[#0F172A]/85 text-base md:text-lg leading-relaxed">
                For trips into Canada we do not sell coverage that travels with the van.
                The renter is responsible for carrying a{" "}
                <strong>personal auto insurance policy that covers this rental vehicle in
                Canada</strong>, including liability at or above the minimums of every
                province you'll drive through. Before your trip, call your insurance agent,
                confirm that a rented 12 or 15 passenger van is covered, confirm that
                coverage applies in Canada, and request the Canadian Non-Resident
                Inter-Province Motor Vehicle Liability Insurance Card.
              </p>
              <p className="mt-4 text-[#0F172A]/85 text-base md:text-lg leading-relaxed">
                Send us proof of that coverage before pickup. Without it, the van isn't
                authorized to cross, and a claim in Canada would fall on you personally.
              </p>
            </div>
          </div>
        </section>

        <section className="px-6 pb-16 md:pb-20">
          <div className="max-w-4xl mx-auto">
            <h2 className="font-display font-black text-3xl md:text-4xl text-[#0F172A] tracking-tight mb-8">
              What you need before you cross
            </h2>
            <div className="grid gap-5 md:grid-cols-2">
              {requirements.map((r) => (
                <div key={r.title} className="border border-[#0F172A]/10 p-6">
                  <r.icon className="text-[#F97316]" size={24} />
                  <h3 className="font-display font-black text-lg text-[#0F172A] mt-4 tracking-tight">
                    {r.title}
                  </h3>
                  <p className="mt-2 text-[#0F172A]/80 leading-relaxed">{r.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="px-6 pb-16 md:pb-20">
          <div className="max-w-4xl mx-auto">
            <h2 className="font-display font-black text-3xl md:text-4xl text-[#0F172A] tracking-tight mb-6">
              How the process works
            </h2>
            <ol className="space-y-4">
              {[
                "Tell us at booking that your trip enters Canada, with your crossing point and dates.",
                "Call your insurance agent and confirm the policy covers a rented passenger van in Canada.",
                "Request the Canadian yellow insurance card and send us a copy of your declarations page.",
                "We add written Canada authorization to your rental agreement.",
                "Cross with passports, the yellow card, and the authorization letter in the van.",
              ].map((step, i) => (
                <li key={i} className="flex gap-4">
                  <CheckCircle2 className="text-[#F97316] shrink-0 mt-1" size={20} />
                  <span className="text-[#0F172A]/85 text-base md:text-lg leading-relaxed">
                    {step}
                  </span>
                </li>
              ))}
            </ol>
            <p className="mt-8 text-[#0F172A]/70 text-sm leading-relaxed">
              Heading south instead?{" "}
              <Link to="/mexico-travel" className="text-[#F97316] font-semibold underline">
                See our Mexico cross-border insurance requirements
              </Link>
              .
            </p>
          </div>
        </section>

        <SeoLongform
          title="Renting a 15 passenger van in the U.S. for a trip to Canada"
          paragraphs={[
            "Canada trips are among the most popular long-haul routes for our passenger vans: Seattle and Bellingham groups running up to Vancouver and Whistler, Detroit crews crossing to Windsor and Toronto, Buffalo families heading to Niagara Falls, and Montana and North Dakota travelers driving into Alberta and Manitoba. A 12 or 15 passenger Sprinter or Transit is the right vehicle for those trips because it keeps the whole group and their luggage in one vehicle, with one driver and one border inspection instead of a caravan.",
            "The insurance rule for Canada is different from Mexico, and it's the part renters most often get wrong. Canada recognizes valid U.S. auto insurance, so there is no requirement to buy a separate Canadian policy the way there is south of the border. What Canada does require is that the driver carry real, verifiable liability coverage — which means the renter must supply their own personal auto policy that extends to the rented van while it is in Canada. Not every personal policy does. Large passenger vans are frequently excluded by vehicle class, and some policies limit coverage to the United States.",
            "The practical step is a phone call to your agent well before your pickup date. Ask three specific questions: does my policy cover a rental vehicle, does it cover a 12 or 15 passenger van specifically, and does it apply in Canada. If all three answers are yes, ask for the Canadian Non-Resident Inter-Province Motor Vehicle Liability Insurance Card — the yellow card — which is the document Canadian officers and provincial police expect to see. Keep a printed copy in the van along with your rental agreement and cross-border authorization letter.",
            "Beyond insurance, plan for the border itself. Every passenger needs a passport, NEXUS card, or enhanced driver's license. Declare any alcohol, food, or firearms honestly — firearms in particular are heavily restricted. Expect longer inspections for a full 15 passenger van than for a sedan, and cross early in the day at busy crossings. Call us and we'll confirm your van, prepare the authorization paperwork, and make sure everything is in order before you leave.",
          ]}
        />
      <GeoAnswerBlock />
      </div>
    </main>
  );
}
